/**
 * Store pour la gestion des sélections dans les tableaux avec pagination côté serveur.
 *
 * Deux modes de fonctionnement :
 * 1. Mode "sélection manuelle" (allChecked = false) :
 *    - checkedRows contient les lignes cochées manuellement
 *    - checkedParents contient les lignes parentes cochées avec leur counter (nombre total d'enfants)
 *
 * 2. Mode "tout coché" (allChecked = true) :
 *    - Toutes les lignes sont considérées cochées
 *    - allIds contient la liste complète des IDs (venant de all_ids de l'API)
 *    - excludedIds contient les IDs des lignes décochées manuellement après le "tout cocher"
 *    - Les getters retournent allIds filtré par excludedIds
 */
export default {
  namespaced: true,
  state: {
    /**
     * Contient une map d'objets de ligne indexés par leur id.
     * Utilisé uniquement en mode "sélection manuelle" (allChecked = false).
     */
    checkedRows: {},
    /**
     * Contient une map des lignes parentes cochées avec leur counter.
     * { [parentId]: { counter: number, checkedChildrenIds: [] } }
     * Permet de compter correctement même si tous les enfants ne sont pas chargés.
     */
    checkedParents: {},
    /**
     * Indique si l'utilisateur a activé le mode "tout cocher".
     */
    allChecked: false,
    /**
     * Contient les IDs des lignes exclues de la sélection globale.
     * Utilisé uniquement en mode "tout coché" (allChecked = true).
     * Quand l'utilisateur décoche une ligne après avoir tout coché, son ID est ajouté ici.
     */
    excludedIds: [],
    /**
     * Liste complète de tous les IDs de la table (venant de all_ids de l'API).
     * Permet de connaître tous les IDs même avec pagination côté serveur.
     */
    allIds: [],
    /**
     * Nombre total de lignes dans la table (venant de l'API).
     */
    totalRows: 0,
  },
  getters: {
    /**
     * Retourne un tableau des objets de lignes cochées.
     * En mode "tout coché", retourne la liste des IDs sélectionnés (allIds moins excludedIds).
     */
    checkedRows(state) {
      if (state.allChecked) {
        return state.allIds.filter((id) => !state.excludedIds.includes(id)).map((id) => ({ id, _checked: true }));
      }
      return Object.values(state.checkedRows);
    },
    /**
     * Retourne un tableau des identifiants des lignes cochées.
     * En mode "tout coché", retourne la liste des IDs sélectionnés (allIds moins excludedIds).
     */
    checkedRowsIds(state) {
      if (state.allChecked) {
        return state.allIds.filter((id) => !state.excludedIds.includes(id));
      }

      return Object.keys(state.checkedRows)
        .filter((id) => state.checkedRows[id] && state.checkedRows[id]._checked)
        .map((id) => {
          const n = Number(id);
          return Number.isFinite(n) && String(n) === String(id) ? n : id;
        });
    },
    /**
     * Retourne le nombre de lignes cochées.
     * Pour les lignes parentes, utilise le counter (nombre total d'enfants) et non le nombre d'enfants chargés.
     */
    nbCheckedRows(state, getters) {
      if (state.allChecked) {
        return state.allIds.length - state.excludedIds.length;
      }

      // Compter les lignes simples cochées
      let count = getters.checkedRowsIds.length;

      // Pour chaque parent coché, ajouter son counter (nombre total d'enfants)
      // et soustraire les enfants déjà comptés individuellement dans checkedRows
      Object.values(state.checkedParents).map((parent) => {
        if (parent.allChildrenChecked) {
          // Si tous les enfants sont cochés, utiliser le counter
          count += parent.counter;
          // Soustraire les enfants qui sont aussi dans checkedRows (pour éviter le double comptage)
          count -= parent.checkedChildrenIds.filter((id) => state.checkedRows[id]).length;
        }
        return parent;
      });

      return count;
    },
    /**
     * Retourne l'état de la case "tout cocher".
     */
    allChecked(state) {
      return state.allChecked;
    },
    /**
     * Retourne les IDs exclus de la sélection globale.
     */
    excludedIds(state) {
      return state.excludedIds;
    },
    /**
     * Retourne la liste complète de tous les IDs.
     */
    allIds(state) {
      return state.allIds;
    },
    /**
     * Retourne le nombre total de lignes dans la table.
     */
    totalRows(state) {
      return state.totalRows;
    },
    /**
     * Retourne les parents cochés.
     */
    checkedParents(state) {
      return state.checkedParents;
    },
    /**
     * Vérifie si une ligne est cochée (en tenant compte du mode).
     * @returns {Function} Fonction qui prend un ID et retourne true si la ligne est cochée.
     */
    isRowChecked: (state) => (id) => {
      if (state.allChecked) {
        // En mode "tout coché", la ligne est cochée sauf si elle est dans les exclus
        return !state.excludedIds.includes(id);
      }
      // En mode manuel, on vérifie dans checkedRows
      return !!state.checkedRows[id]?._checked;
    },
  },
  mutations: {
    /**
     * Coche une ligne (ajoute à checkedRows ou retire des excludedIds).
     */
    checkRow(state, row) {
      if (state.allChecked) {
        // En mode "tout coché", on retire l'ID des exclus
        const index = state.excludedIds.indexOf(row.id);
        if (index > -1) {
          state.excludedIds.splice(index, 1);
        }
      } else {
        // En mode manuel, on ajoute à checkedRows
        state.checkedRows[row.id] = { ...row, _checked: true };
      }
    },
    /**
     * Décoche une ligne (retire de checkedRows ou ajoute aux excludedIds).
     */
    uncheckRow(state, row) {
      if (state.allChecked) {
        // En mode "tout coché", on ajoute l'ID aux exclus
        if (!state.excludedIds.includes(row.id)) {
          state.excludedIds.push(row.id);
        }
        // Si tous les éléments sont exclus, on sort du mode "tout coché"
        if (state.excludedIds.length >= state.allIds.length) {
          state.allChecked = false;
          state.excludedIds = [];
        }
      } else {
        // En mode manuel, on retire de checkedRows
        delete state.checkedRows[row.id];
      }
    },
    /**
     * Coche une ligne parente avec tous ses enfants.
     * @param {Object} payload - { parentId, counter, children, allChildrenIds }
     * allChildrenIds contient tous les ids des enfants (pas uniquement ceux chargés/paginés en front).
     * Si absent, on retombe sur les ids des enfants chargés.
     */
    checkParent(state, {
      parentId, counter, children, allChildrenIds,
    }) {
      const loadedIds = children.map((child) => child.id);
      const childrenIds = allChildrenIds?.length ? allChildrenIds : loadedIds;
      state.checkedParents[parentId] = {
        counter,
        checkedChildrenIds: childrenIds,
        allChildrenChecked: true,
      };

      if (state.allChecked) {
        // En mode "tout coché", retirer les enfants des exclus
        childrenIds.map((id) => {
          const index = state.excludedIds.indexOf(id);
          if (index > -1) {
            state.excludedIds.splice(index, 1);
          }
          return id;
        });
      } else {
        // En mode manuel, ajouter les enfants chargés à checkedRows avec l'objet complet
        children.map((child) => {
          state.checkedRows[child.id] = { ...child, _checked: true };
          return child;
        });
        // Ajouter les enfants non chargés (hors page courante) avec un objet minimal
        childrenIds.map((id) => {
          if (!loadedIds.includes(id)) {
            state.checkedRows[id] = { id, _checked: true };
          }
          return id;
        });
      }
    },
    /**
     * Décoche une ligne parente et tous ses enfants.
     * @param {Object} payload - { parentId, childrenIds }
     */
    uncheckParent(state, { parentId, childrenIds }) {
      delete state.checkedParents[parentId];

      if (state.allChecked) {
        // En mode "tout coché", ajouter les enfants aux exclus
        childrenIds.map((id) => {
          if (!state.excludedIds.includes(id)) {
            state.excludedIds.push(id);
          }
          return id;
        });
      } else {
        // En mode manuel, retirer les enfants de checkedRows
        childrenIds.map((id) => {
          delete state.checkedRows[id];
          return id;
        });
      }
    },
    /**
     * Met à jour l'état d'un parent quand un enfant est décoché individuellement.
     * @param {Object} payload - { parentId, childId }
     */
    uncheckChildFromParent(state, { parentId, childId }) {
      const parent = state.checkedParents[parentId];
      if (parent) {
        // Retirer l'enfant de la liste
        const index = parent.checkedChildrenIds.indexOf(childId);
        if (index > -1) {
          parent.checkedChildrenIds.splice(index, 1);
        }
        // Marquer que tous les enfants ne sont plus cochés
        parent.allChildrenChecked = false;

        // Si plus aucun enfant n'est coché, supprimer le parent
        if (parent.checkedChildrenIds.length === 0) {
          delete state.checkedParents[parentId];
        }
      }
    },
    /**
     * Active le mode "tout coché".
     */
    checkAll(state) {
      state.allChecked = true;
      state.excludedIds = [];
      // On vide checkedRows et checkedParents car on passe en mode "tout coché"
      state.checkedRows = {};
      state.checkedParents = {};
    },
    /**
     * Désactive le mode "tout coché" et vide la sélection.
     */
    uncheckAll(state) {
      state.allChecked = false;
      state.excludedIds = [];
      state.checkedRows = {};
      state.checkedParents = {};
    },
    /**
     * Met à jour le nombre total de lignes.
     */
    setTotalRows(state, totalRows) {
      state.totalRows = totalRows;
    },
    /**
     * Met à jour la liste complète de tous les IDs (venant de all_ids de l'API).
     */
    setAllIds(state, allIds) {
      state.allIds = allIds || [];
    },
    /**
     * Synchronise l'état des lignes visibles avec le store.
     * Appelé quand les rows changent (changement de page, etc.).
     */
    syncVisibleRows(state, rows) {
      if (!state.allChecked) {
        // En mode manuel, on met à jour checkedRows avec les lignes visibles
        const processRow = (row) => {
          if (row._checked && !row._mixed) {
            state.checkedRows[row.id] = row;
          } else if (state.checkedRows[row.id] && !row._checked) {
            delete state.checkedRows[row.id];
          }
          if (row._children?.length) {
            row._children.map((child) => processRow(child));
          }
        };
        rows.map((row) => processRow(row));
      }
    },
  },
  actions: {
    /**
     * Vide toute la sélection.
     */
    clearCheckedRows({ commit }) {
      commit("uncheckAll");
    },
    /**
     * Gère le changement d'état d'une checkbox individuelle.
     */
    toggleRow({ commit }, { row, checked }) {
      if (checked) {
        commit("checkRow", row);
      } else {
        commit("uncheckRow", row);
      }
    },
    /**
     * Gère le cochage/décochage d'une ligne parente (avec enfants).
     * @param {Object} payload - { row, checked, counter }
     */
    toggleParent({ commit }, { row, checked, counter }) {
      const children = row._children || [];
      // row._allChildrenIds (venant de l'API, ex: org.devis.all_ids) contient tous les ids,
      // même ceux non chargés en front (pagination du sous-tableau).
      const allChildrenIds = row._allChildrenIds?.length ? row._allChildrenIds : children.map((child) => child.id);

      if (checked) {
        commit("checkParent", {
          parentId: row.id,
          counter: counter || row.counter || children.length,
          children,
          allChildrenIds,
        });
      } else {
        commit("uncheckParent", {
          parentId: row.id,
          childrenIds: allChildrenIds,
        });
      }
    },
    /**
     * Gère le clic sur "tout cocher/décocher".
     */
    toggleAll({ commit, state }) {
      if (state.allChecked && state.excludedIds.length === 0) {
        // Si tout est coché sans exclusion, on décoche tout
        commit("uncheckAll");
      } else {
        // Sinon on coche tout
        commit("checkAll");
      }
    },
  },
};
